import { useState, type FormEvent, type ReactNode } from 'react';
import {
  devices as devicesApi,
  loxone,
  type Balik,
  type Mapovanie,
  type Miniserver,
  type Zariadenie,
} from '../api/client.js';
import { useApi } from '../api/use-api.js';
import { EmptyState } from '../components/states.js';
import { Badge, Button, Card, Notice, PageHeader, SectionTitle } from '../components/ui.js';
import { IconDownload, IconLink, IconPlus, IconTrash } from '../components/icons.js';
import { useSession } from '../auth/session.js';
import { Data } from './common.js';

/*
 * Mapovania zariadeni na Miniservery.
 *
 * Sablona sa stahuje ako subor, lebo do Loxone Config sa importuje zo
 * suboru. Pokyny su rovno vedla nej - kroky v Config sa nedaju obist
 * a bez nich objekt v Miniserveri nevznikne.
 */

function stiahniBlob(fileName: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const odkaz = document.createElement('a');
  odkaz.href = url;
  odkaz.download = fileName;
  odkaz.click();
  URL.revokeObjectURL(url);
}

function stiahni(fileName: string, obsah: string): void {
  stiahniBlob(fileName, new Blob([obsah], { type: 'application/xml' }));
}

/** Balíček `.lxaddon` chodí ako base64, lebo je to ZIP. */
function stiahniAddon(fileName: string, base64: string): void {
  const bajty = Uint8Array.from(atob(base64), (z) => z.charCodeAt(0));
  stiahniBlob(fileName, new Blob([bajty], { type: 'application/zip' }));
}

function SablonaNaStiahnutie({ balik }: { readonly balik: Balik }): ReactNode {
  return (
    <div className="flex flex-col gap-3 rounded-(--radius-control) bg-(--color-surface-sunken) p-4">
      <SectionTitle>Šablóny pre Loxone Config</SectionTitle>
      {/*
       * Balicky `.lxaddon` su PRVA volba: overene v prevadzke 2026-09-07,
       * ze sa nainstaluju dvojklikom. Rucne kopirovanie XML zostava ako
       * zaloha.
       *
       * Ponuka sa len to, co zariadenie naozaj ma. Teplomer sa neda
       * ovladat, takze tlacidlo na virtualny vystup by stiahlo subor,
       * z ktoreho by v Loxone vznikol objekt bez jedineho prikazu.
       */}
      {balik.addons.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap gap-2">
            {balik.addons.map((a) => (
              <Button
                key={a.fileName}
                variant="primary"
                icon={<IconDownload className="size-4" />}
                onClick={() => stiahniAddon(a.fileName, a.base64)}
              >
                {a.fileName.endsWith('-vystup.lxaddon')
                  ? 'Výstup ako .lxaddon'
                  : 'Vstup ako .lxaddon'}
              </Button>
            ))}
          </div>
          <p className="text-xs text-(--color-text-subtle)">
            Stiahni a otvor dvojklikom — Loxone Config si šablónu naimportuje sám. Netreba kopírovať
            súbory ani reštartovať Config. Každá šablóna má vlastný balíček.
          </p>
        </div>
      )}

      {balik.output === null && balik.input === null && (
        <Notice tone="warn">
          Toto zariadenie nemá čo posielať do Loxone ani čo ovládať — šablóna by bola prázdna.
        </Notice>
      )}

      {(balik.output !== null || balik.input !== null) && (
        <div className="flex flex-col gap-2">
          <span className="text-xs font-medium text-(--color-text)">Alebo súbory ručne</span>
          <div className="flex flex-wrap gap-2">
            {balik.output !== null && (
              <Button
                variant="ghost"
                icon={<IconDownload className="size-4" />}
                onClick={() => stiahni(balik.output?.fileName ?? '', balik.output?.xml ?? '')}
              >
                Virtuálny výstup
              </Button>
            )}
            {balik.input !== null && (
              <Button
                variant="ghost"
                icon={<IconDownload className="size-4" />}
                onClick={() => stiahni(balik.input?.fileName ?? '', balik.input?.xml ?? '')}
              >
                Virtuálny vstup
              </Button>
            )}
          </div>
        </div>
      )}

      <pre className="min-w-0 overflow-x-auto rounded-(--radius-control) bg-(--color-surface) p-3 font-mono text-xs break-words whitespace-pre-wrap text-(--color-text-muted)">
        {balik.instructions}
      </pre>
    </div>
  );
}

function FormularMapovania({
  zariadenia,
  miniservery,
  poVytvoreni,
  zavri,
}: {
  readonly zariadenia: readonly Zariadenie[];
  readonly miniservery: readonly Miniserver[];
  readonly poVytvoreni: () => void;
  readonly zavri: () => void;
}): ReactNode {
  const [deviceId, setDeviceId] = useState(zariadenia[0]?.id ?? '');
  const [miniserverId, setMiniserverId] = useState(miniservery[0]?.id ?? '');
  const [transport, setTransport] = useState<Mapovanie['transport']>('udp-batch');
  const [balik, setBalik] = useState<Balik | undefined>(undefined);
  const [chyba, setChyba] = useState<string | undefined>(undefined);
  const [prebieha, setPrebieha] = useState(false);

  const odosli = (event: FormEvent) => {
    event.preventDefault();
    setChyba(undefined);
    setPrebieha(true);
    void loxone
      .createMapping(deviceId, miniserverId, transport)
      .then((vysledok) => {
        setBalik(vysledok.bundle);
        poVytvoreni();
      })
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Mapovanie sa nepodarilo vytvoriť.');
      })
      .finally(() => setPrebieha(false));
  };

  const vyber =
    'w-full rounded-(--radius-control) border border-(--color-border-strong) bg-(--color-surface) px-3 py-2 text-sm text-(--color-text)';

  return (
    <Card className="flex flex-col gap-4 p-4 sm:p-5">
      <SectionTitle>Nové priradenie</SectionTitle>

      {miniservery.length === 0 ? (
        <Notice tone="warn">
          Najprv spáruj Miniserver na obrazovke Loxone; bez neho nie je kam zariadenie priradiť.
        </Notice>
      ) : (
        <form onSubmit={odosli} className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5 text-sm font-medium text-(--color-text)">
              Zariadenie
              <select
                className={vyber}
                value={deviceId}
                onChange={(e) => setDeviceId(e.target.value)}
              >
                {zariadenia.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1.5 text-sm font-medium text-(--color-text)">
              Miniserver
              <select
                className={vyber}
                value={miniserverId}
                onChange={(e) => setMiniserverId(e.target.value)}
              >
                {miniservery.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1.5 text-sm font-medium text-(--color-text)">
              Doručovanie stavov
              <select
                className={vyber}
                value={transport}
                onChange={(e) => setTransport(e.target.value as Mapovanie['transport'])}
              >
                <option value="udp-batch">Dávkové UDP (odporúčané)</option>
                <option value="http-poll">HTTP polling</option>
              </select>
              <span className="text-xs font-normal text-(--color-text-subtle)">
                Pri stovke zariadení je jeden UDP paket na zariadenie výrazne šetrnejší než stovky
                HTTP dotazov.
              </span>
            </label>
          </div>

          {chyba !== undefined && <Notice tone="danger">{chyba}</Notice>}

          <div className="flex flex-wrap gap-2">
            <Button type="submit" variant="primary" disabled={prebieha || deviceId === ''}>
              {prebieha ? 'Vytváram…' : 'Priradiť a vygenerovať šablóny'}
            </Button>
            <Button variant="ghost" onClick={zavri} disabled={prebieha}>
              Zavrieť
            </Button>
          </div>
        </form>
      )}

      {balik && <SablonaNaStiahnutie balik={balik} />}
    </Card>
  );
}

function RiadokMapovania({
  mapovanie,
  nazovZariadenia,
  nazovMiniservera,
  poZmene,
}: {
  readonly mapovanie: Mapovanie;
  readonly nazovZariadenia: string;
  readonly nazovMiniservera: string;
  readonly poZmene: () => void;
}): ReactNode {
  const { user } = useSession();
  const jeSpravca = user?.role === 'admin';
  const [balik, setBalik] = useState<Balik | undefined>(undefined);
  const [chyba, setChyba] = useState<string | undefined>(undefined);

  const stiahniSablonu = () => {
    setChyba(undefined);
    void loxone
      .template(mapovanie.id)
      .then((v) => setBalik(v.bundle))
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Šablónu sa nepodarilo získať.');
      });
  };

  const zrus = () => {
    setChyba(undefined);
    void loxone
      .removeMapping(mapovanie.id)
      .then(poZmene)
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Mapovanie sa nepodarilo zrušiť.');
      });
  };

  return (
    <Card as="li" className="flex flex-col gap-3 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="text-(--color-text-subtle)">
            <IconLink />
          </span>
          <div className="flex min-w-0 flex-col">
            <span className="truncate font-medium text-(--color-text)">
              {nazovZariadenia} <span className="text-(--color-text-subtle)">→</span>{' '}
              {nazovMiniservera}
            </span>
            <span
              className="truncate font-mono text-xs text-(--color-text-subtle)"
              title="Názov objektu v Loxone Config"
            >
              {mapovanie.virtualInputName}
            </span>
          </div>
        </div>
        <Badge tone="neutral">
          {mapovanie.transport === 'udp-batch' ? 'dávkové UDP' : 'HTTP polling'}
        </Badge>
      </div>

      {chyba !== undefined && <Notice tone="danger">{chyba}</Notice>}
      {balik && <SablonaNaStiahnutie balik={balik} />}

      {/*
       * Posledny odoslany paket.
       *
       * Ked sa hodnota v Loxone nemeni, toto rozhodne, na ktorej strane
       * chybu hladat: ked je hodnota v pakete a v Loxone nie, chyba je
       * v sablone alebo v jej importe, nie v brane.
       */}
      {mapovanie.lastPayload !== null && (
        <details className="flex flex-col gap-1">
          <summary className="cursor-pointer text-xs text-(--color-text-subtle)">
            Posledný odoslaný paket
            {mapovanie.lastSentAt !== null &&
              ` · ${new Date(mapovanie.lastSentAt).toLocaleTimeString('sk-SK')}`}
          </summary>
          {/*
           * Nie len posledny paket, ale kratka historia.
           *
           * Impulz tlacidla trva 400 ms a hned po nom ide navratovy paket
           * so samymi nulami - kto sa pozrel po stlaceni, videl nuly
           * a vyzeralo to, ze sa nic neposlalo (2026-09-15).
           */}
          <ul className="mt-1 flex flex-col gap-1">
            {(mapovanie.recentPayloads.length > 0
              ? mapovanie.recentPayloads
              : [{ payload: mapovanie.lastPayload, at: mapovanie.lastSentAt ?? 0 }]
            ).map((p) => (
              <li key={`${String(p.at)}-${p.payload ?? ''}`} className="flex flex-col">
                <span className="font-mono text-[0.7rem] text-(--color-text-subtle)">
                  {new Date(p.at).toLocaleTimeString('sk-SK')}
                </span>
                <code className="block overflow-x-auto rounded-(--radius-control) bg-(--color-surface-sunken) p-2 font-mono text-xs break-all text-(--color-text-muted)">
                  {p.payload}
                </code>
              </li>
            ))}
          </ul>
          <p className="text-xs text-(--color-text-subtle)">
            Toto brána naozaj odoslala. Pri tlačidle hľadaj riadok s <code>act_…=1</code> — impulz
            trvá 400 ms a hneď po ňom ide návratový paket so samými nulami. Keď je hodnota tu a v
            Loxone sa nemení, chyba je v šablóne alebo v jej importe — vygeneruj ju a naimportuj
            znova.
          </p>
        </details>
      )}

      {jeSpravca && (
        <div className="flex flex-wrap gap-2">
          <Button icon={<IconDownload className="size-4" />} onClick={stiahniSablonu}>
            Šablóny
          </Button>
          <Button variant="danger" icon={<IconTrash className="size-4" />} onClick={zrus}>
            Zrušiť priradenie
          </Button>
        </div>
      )}
    </Card>
  );
}

export function Mappings(): ReactNode {
  const { user } = useSession();
  const jeSpravca = user?.role === 'admin';
  const [pridava, setPridava] = useState(false);

  const [stahuje, setStahuje] = useState(false);
  const [chybaArchivu, setChybaArchivu] = useState<string | undefined>(undefined);

  const mapovania = useApi((signal) => loxone.mappings(signal));
  const zariadenia = useApi((signal) => devicesApi.list(signal));
  const miniservery = useApi((signal) => loxone.miniservers(signal));

  /*
   * Archiv so vsetkymi sablonami, rozdeleny do `VirtualIn` a `VirtualOut` -
   * presne tak, ako ich caka Loxone Config vo svojich priecinkoch.
   */
  const stiahniVsetky = (): void => {
    setChybaArchivu(undefined);
    setStahuje(true);
    void loxone
      .vsetkySablony()
      .then(({ fileName, base64 }) => stiahniAddon(fileName, base64))
      .catch((error: unknown) => {
        setChybaArchivu(error instanceof Error ? error.message : 'Archív sa nepodarilo pripraviť.');
      })
      .finally(() => setStahuje(false));
  };

  const nazovZariadenia = (id: string): string =>
    zariadenia.data?.devices.find((d) => d.id === id)?.name ?? id;
  const nazovMiniservera = (id: string): string =>
    miniservery.data?.miniservers.find((m) => m.id === id)?.name ?? id;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Mapovania"
        detail="Ktoré zariadenie posiela stavy do ktorého Miniservera."
        action={
          jeSpravca ? (
            <div className="flex flex-wrap gap-2">
              {/*
               * Hromadne stiahnutie. Ked sa zmeni tvar sablony, treba
               * naimportovat vsetko znova - jedno rozbalenie namiesto
               * klikania cez kazde zariadenie.
               */}
              <Button
                icon={<IconDownload className="size-4" />}
                onClick={stiahniVsetky}
                disabled={stahuje}
              >
                {stahuje ? 'Pripravujem…' : 'Všetky šablóny (ZIP)'}
              </Button>
              <Button
                variant="primary"
                icon={<IconPlus className="size-4" />}
                onClick={() => setPridava(true)}
                disabled={pridava}
              >
                Priradiť zariadenie
              </Button>
            </div>
          ) : undefined
        }
      />

      {chybaArchivu !== undefined && <Notice tone="danger">{chybaArchivu}</Notice>}

      <Notice tone="warn">
        <p className="font-medium">Názov objektu v Loxone sa mení len do prvého importu.</p>
        <p className="mt-1">
          Kým objekt v Miniserveri nevznikne, ide názov za menom zariadenia. Po importe je pevný:
          premenovanie v LoxLinku objekt v Loxone Config nepremenuje a šablóna s iným názvom by po
          importe založila druhý objekt. Keď ho chceš premenovať aj v Loxone, zruš mapovanie, vytvor
          nové a starý objekt v Loxone Config zmaž.
        </p>
      </Notice>

      {pridava && (
        <FormularMapovania
          zariadenia={zariadenia.data?.devices ?? []}
          miniservery={miniservery.data?.miniservers ?? []}
          poVytvoreni={mapovania.reload}
          zavri={() => setPridava(false)}
        />
      )}

      <Data nacitanie={mapovania}>
        {(data) =>
          data.mappings.length === 0 ? (
            <EmptyState
              title="Žiadne priradenia"
              detail={
                jeSpravca
                  ? 'Priraď zariadenie Miniserveru; LoxLink k nemu rovno vygeneruje šablóny do Loxone Config.'
                  : 'Priradenie môže vytvoriť len správca.'
              }
            />
          ) : (
            <ul className="flex flex-col gap-3">
              {data.mappings.map((m) => (
                <RiadokMapovania
                  key={m.id}
                  mapovanie={m}
                  nazovZariadenia={nazovZariadenia(m.deviceId)}
                  nazovMiniservera={nazovMiniservera(m.miniserverId)}
                  poZmene={mapovania.reload}
                />
              ))}
            </ul>
          )
        }
      </Data>
    </div>
  );
}
